/* eslint-disable */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';

export interface IParcelaCarne {
  numero: string;
  parcela: string | null;
  vencimento: Date | string;
  valor: number;
  status: boolean;
  datapagamento: Date | string | null;
  formapagamento: string | null;
}

export interface IDadosCarne {
  cliente: string;
  cpf?: string;
  numeroVenda: string;
  logo?: string;
  // Nome/razão social de quem está emitindo o carnê (a empresa, não o
  // cliente) — vira o "cabeçalho" ao lado da logo, tipo papel timbrado.
  // Sem isso o carnê antigo não dizia de qual empresa ele era.
  empresaFantasia?: string;
  empresaRazaoSocial?: string;
  parcelas: IParcelaCarne[];
}

// Mesmo carregamento de logo (com fallback gerado em canvas) de
// ComprovanteBaixa.ts/ImpressaoReceber.ts — repetido em vez de importado
// porque esses arquivos ficam em outra pasta (Financeiro/ContasReceber) e o
// próprio padrão do projeto já é duplicar essa função em cada gerador de PDF.
async function getBase64ImageFromUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Erro canvas');
      canvas.height = img.naturalHeight;
      canvas.width = img.naturalWidth;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject('Erro ao carregar logo');
    img.src = url;
  });
}

function gerarLogoPadrao(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 90;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const gradient = ctx.createLinearGradient(0, 0, 320, 0);
  gradient.addColorStop(0, '#1f2937');
  gradient.addColorStop(1, '#374151');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.beginPath();
  ctx.arc(45, 45, 25, 0, 2 * Math.PI);
  ctx.fillStyle = '#10b981';
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 20px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('CARNÊ', 90, 45);
  return canvas.toDataURL('image/png');
}

const formatarMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarData = (data: Date | string | null | undefined) =>
  data ? moment(data).format('DD/MM/YYYY') : '—';

const MESES_ABREV = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const formatarMesAno = (data: Date | string | null | undefined) => {
  if (!data) return '—';
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return '—';
  return `${MESES_ABREV[d.getMonth()]}/${d.getFullYear()}`;
};

// Um card do carnê ("um comprovante") — cabeçalho com logo/nome da empresa +
// título + selo "Nº/Emissão" + selo "PARCELA/MÊS-ANO", corpo em duas colunas
// (dados do cliente/venda | vencimento/valor/situação), observações e
// linhas de assinatura por baixo. Tudo dentro de uma borda única (moldura),
// pra parecer um canhoto de verdade e não só texto solto na folha.
function montarCard(imageData: string, dados: IDadosCarne, item: IParcelaCarne, indice: number): any {
  const pago = item.status === true;
  // Cores fixas de "pago" — o selo só é desenhado quando pago (ver stack de
  // Valor abaixo), então não precisa mais da variante "a vencer".
  const corSituacao = '#059669';
  const fundoSituacao = '#d1fae5';
  const textoSituacao = 'PAGO';
  const numeroComprovante = String(indice + 1).padStart(6, '0');
  const nomeEmpresa = dados.empresaFantasia || dados.empresaRazaoSocial || '';
  const mesAno = formatarMesAno(item.vencimento);
  const numeroParcela = item.parcela || `${indice + 1}/${dados.parcelas.length}`;

  const miolo = {
    stack: [
      // Faixa de destaque no topo — só um traço de cor pra dar acabamento de
      // papel timbrado, sem precisar de mais texto/elemento.
      {
        canvas: [{ type: 'rect', x: 0, y: 0, w: 495, h: 4, color: '#1f2937' }],
        margin: [0, 0, 0, 10],
      },
      // Cabeçalho: logo + nome da empresa | título do comprovante | selo Nº/Emissão | selo Parcela/Mês-Ano
      {
        columns: [
          { image: imageData, width: 46, alignment: 'left' },
          {
            width: 150,
            stack: [
              ...(nomeEmpresa ? [{ text: nomeEmpresa, bold: true, fontSize: 12 }] : []),
              ...(dados.empresaRazaoSocial && dados.empresaRazaoSocial !== nomeEmpresa
                ? [{ text: dados.empresaRazaoSocial, fontSize: 6, color: '#6b7280' }]
                : []),
            ],
            margin: [8, 3, 0, 0],
          },
          {
            width: '*',
            stack: [
              { text: 'COMPROVANTE DE PAGAMENTO', bold: true, fontSize: 11, color: '#1f2937' },
            ],
            margin: [4, 6, 0, 0],
          },
          {
            width: 95,
            table: {
              body: [
                [{ text: `Nº ${numeroComprovante}`, fontSize: 8, alignment: 'center', color: '#374151', bold: true }],
                [{ text: `Emissão: ${moment().format('DD/MM/YYYY')}`, fontSize: 6.5, alignment: 'center', color: '#6b7280' }],
              ],
            },
            layout: {
              fillColor: () => '#f3f4f6',
              hLineWidth: () => 0,
              vLineWidth: () => 0,
              paddingTop: () => 3,
              paddingBottom: () => 3,
            },
          },
          // Selo de parcela/mês — um bloco único (não mais duas caixas
          // empilhadas com espaço entre elas), com o número da parcela como
          // elemento principal (maior) e o mês/ano como legenda logo abaixo,
          // separados por um traço fino — mesma leitura de um "canhoto" de
          // carnê físico.
          {
            width: 82,
            table: {
              widths: ['*'],
              body: [
                [{ text: 'PARCELA', fontSize: 6, alignment: 'center', color: '#cbd5e1', margin: [0, 4, 0, 0] }],
                [{ text: numeroParcela, fontSize: 18, bold: true, alignment: 'center', color: 'white', margin: [0, 0, 0, 3] }],
                [{ text: mesAno, fontSize: 8, bold: true, alignment: 'center', color: '#e5e7eb', margin: [0, 3, 0, 4] }],
              ],
            },
            layout: {
              fillColor: () => '#1f2937',
              hLineWidth: (rowIndex: number) => (rowIndex === 2 ? 1 : 0),
              vLineWidth: () => 0,
              hLineColor: () => '#4b5563',
              paddingLeft: () => 2,
              paddingRight: () => 2,
              paddingTop: () => 0,
              paddingBottom: () => 0,
            },
          },
        ],
        columnGap: 6,
        margin: [0, 0, 0, 8],
      },
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 500, y2: 0, lineWidth: 1, lineColor: '#e5e7eb' }],
        margin: [0, 0, 0, 8],
      },
      // Corpo: coluna do cliente/venda | coluna vencimento/valor/situação
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: [{ text: 'Cliente: ', bold: true }, dados.cliente], fontSize: 9, margin: [0, 0, 0, 3] },
              { text: [{ text: 'CPF/CNPJ: ', bold: true }, dados.cpf || '—'], fontSize: 9, margin: [0, 0, 0, 3] },
              { text: [{ text: 'Serviço: ', bold: true }, 'Venda de Produtos'], fontSize: 9, margin: [0, 0, 0, 3] },
              { text: [{ text: 'Referência: ', bold: true }, `Venda Nº ${item.numero || dados.numeroVenda}`], fontSize: 9 },
            ],
          },
          {
            width: 190,
            stack: [
              {
                columns: [
                  {
                    width: '*',
                    stack: [
                      { text: [{ text: 'Vencimento: ', bold: true }, formatarData(item.vencimento)], fontSize: 9, margin: [0, 0, 0, 3] },
                      { text: [{ text: 'Pagamento em: ', bold: true }, pago ? formatarData(item.datapagamento) : '—'], fontSize: 9, margin: [0, 0, 0, 3] },
                      { text: [{ text: 'Forma de pgto: ', bold: true }, pago ? (item.formapagamento || '—') : '—'], fontSize: 9 },
                    ],
                  },
                  {
                    width: 90,
                    stack: [
                      { text: 'Valor', fontSize: 8, color: '#6b7280', alignment: 'right' },
                      { text: formatarMoeda(item.valor), fontSize: 13, bold: true, alignment: 'right', margin: [0, 0, 0, 6] },
                      // Selo de situação só aparece quando já foi pago —
                      // sem o rótulo "A VENCER" pra quem ainda não venceu.
                      ...(pago ? [{
                        table: { body: [[{ text: textoSituacao, bold: true, fontSize: 9, alignment: 'center', color: corSituacao }]] },
                        layout: {
                          fillColor: () => fundoSituacao,
                          hLineWidth: () => 0,
                          vLineWidth: () => 0,
                          paddingTop: () => 3,
                          paddingBottom: () => 3,
                        },
                      }] : []),
                    ],
                  },
                ],
                columnGap: 8,
              },
            ],
          },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 8],
      },
      {
        table: {
          widths: ['*'],
          body: [[{ text: 'Observações:', fontSize: 8, color: '#9ca3af', margin: [4, 12, 0, 0] }]],
        },
        layout: { hLineWidth: () => 0.5, vLineWidth: () => 0.5, hLineColor: () => '#e5e7eb', vLineColor: () => '#e5e7eb' },
        margin: [0, 0, 0, 8],
      },
      {
        columns: [
          {
            width: '*',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 210, y2: 0, lineWidth: 0.5, lineColor: '#9ca3af' }], margin: [0, 14, 0, 2] },
              { text: 'Assinatura do Responsável pela Empresa', fontSize: 7, color: '#6b7280' },
            ],
          },
          {
            width: '*',
            alignment: 'right',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 210, y2: 0, lineWidth: 0.5, lineColor: '#9ca3af' }], margin: [0, 14, 0, 2], alignment: 'right' },
              { text: 'Assinatura do Cliente', fontSize: 7, color: '#6b7280', alignment: 'right' },
            ],
          },
        ],
      },
    ],
    margin: [10, 10, 10, 10],
  };

  // Moldura única em volta do card inteiro (o "canhoto") — table de 1
  // célula só serve pra desenhar a borda ao redor de um conteúdo de altura
  // variável, que é o que pdfmake aceita nativamente (não suporta
  // border-radius, então fica reto nos cantos, não arredondado).
  return {
    table: {
      widths: ['*'],
      body: [[miolo]],
    },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#cbd5e1',
      vLineColor: () => '#cbd5e1',
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [0, 0, 0, 10],
  };
}

// Gera o carnê de pagamento em PDF (1 card por parcela, separados por linha
// pontilhada de corte, todos na mesma folha A4) — chamado a partir da tela de
// Detalhes de Venda com as parcelas (contasreceber) já buscadas por venda
// (ver ContasReceberService.getPorVenda). Venda à vista tem 1 parcela só, e
// o resultado sai com 1 card único; a prazo sai com N.
export async function gerarCarne(dados: IDadosCarne): Promise<Blob> {
  // Precisa ser setado aqui dentro, com esse caminho exato
  // (`pdfFonts.pdfMake.vfs`, não `pdfFonts.vfs`) — é como o pacote
  // `pdfmake` 0.2.7 realmente expõe o vfs (ver impressaovenda01.ts, que já
  // fazia certo). Setar errado deixa `pdfMake.vfs` undefined e quebra
  // qualquer `bold: true` com "File 'Roboto-Medium.ttf' not found".
  (pdfMake as any).fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf',
    },
  };
  if ((pdfFonts as any)?.pdfMake?.vfs) {
    (pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;
  }

  let imageData: string;
  if (dados.logo && dados.logo.trim() !== '') {
    const baseUrl = (window as any).env?.REACT_APP_URL_IMGLOG || '';
    const imageUrl = baseUrl + dados.logo;
    try {
      imageData = await getBase64ImageFromUrl(imageUrl);
    } catch {
      imageData = gerarLogoPadrao();
    }
  } else {
    imageData = gerarLogoPadrao();
  }

  const content: any[] = [];
  dados.parcelas.forEach((item, indice) => {
    content.push(montarCard(imageData, dados, item, indice));
    if (indice < dados.parcelas.length - 1) {
      content.push({
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, dash: { length: 3, space: 2 }, lineColor: '#9ca3af' }],
        margin: [0, 0, 0, 10],
      });
    }
  });

  const documentDefinition: any = {
    pageSize: 'A4',
    pageMargins: [40, 30, 40, 30],
    content,
  };

  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      pdfDocGenerator.getBlob((blob: Blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });
}
